import { useMemo } from "react";
import KDEChart from "../../Shared/KDEChart";
import MaskTable, { extractNumericValue } from "../../Shared/MaskTable";
import useTranslate from "src/utils/useTranslate";
import { Collapse, Descriptions, Table, Typography } from "antd";
import { toTitleCase } from "src/utils/general";
import { useValue } from "src/utils/useValue";
import { StoreState } from "src/store/configureStore";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

type TProps = {
  data: any;
};

const FiberAnalysisAttributes = ({ data }: TProps) => {
  const [t] = useTranslate();
  const isCalibrated = data?.mask_results?.[0]?.calibrated_area?.value;
  const {
    phaseTwoFiberAnalysisData
  } = useSelector((state: StoreState) => state.semAnalysis);
  const params = useParams<any>();

  const maskTableColumns = useMemo(
    () => [
      {
        title: "",
        dataIndex: "id",
        key: "id",
        render: (item: any) => <>{item?.replace("mask_id_", "Mask ")}</>,
        width: "10%",
      },
      {
        title: t("sem.width"),
        dataIndex: "width",
        key: "width",
        render: (item: any) => <>{`${item}`}</>,
        width: "20%",
        sorter: {
          compare: (a: any, b: any) => {
            return extractNumericValue(a.width) - extractNumericValue(b.width)
          },
        },
      },
      {
        title: t("sem.length"),
        dataIndex: isCalibrated ? "calibrated_length" : "max_length",
        key: isCalibrated ? "calibrated_length" : "length",
        render: (item: any) => <>{item?.value || `${item?.pixel_length} px`}</>,
        width: "20%",
        sorter: {
          compare: (a: any, b: any) =>
            isCalibrated
              ? extractNumericValue(a.calibrated_length.value) -
              extractNumericValue(b.calibrated_length.value)
              : a.max_length.pixel_length - b.max_length.pixel_length,
        },
      },
    ],
    [isCalibrated, t]
  );

  return (
    <>
      {data && data?.mask_results?.length && (
        <div style={{
          display: "flex",
          gap: "1rem",
          flexDirection: "column"
        }}>
          <div style={{
            display: "flex",
            gap: "1rem",
          }}>
            <KDEChart
              data={data?.mask_results}
              unit={data?.unit}
              selections={[]}
              handleChartSelection={() => null}
              titles={{
                graphTitle: t("sem.lengthDistribution"),
                yAxis: t("common.count"),
                xAxis: t("sem.length"),
              }}
              showSelections={false}
            />
            <KDEChart
              data={data?.mask_results}
              unit={data?.unit}
              selections={[]}
              handleChartSelection={() => null}
              titles={{
                graphTitle: t("sem.widthDistribution"),
                yAxis: t("common.count"),
                xAxis: t("sem.width"),
              }}
              showSelections={false}
              type="width"
            />
          </div>
          {!!phaseTwoFiberAnalysisData?.[params.fileId]?.data ?
            <div>
              <FiberAnalysisPhaseTwoResult data={phaseTwoFiberAnalysisData?.[params.fileId]?.data} />
            </div>
            :
            <div>
              <h3>{t("sem.totalMasks")} ({data?.mask_results?.length})</h3>
              <MaskTable data={data} columns={maskTableColumns} />
            </div>
          }
        </div>
      )}
    </>
  );
};

export const FiberAnalysisPhaseTwoResult = ({ data }: any) => {
  const [t] = useTranslate()

  const { getValue: getEUValue } = useValue();
  const fiberData = data?.fiber_data ?? []

  const fiberSummaryItems = Object.keys(data?.fiber_summary ?? {}).map((summary) => ({
    key: summary,
    label: toTitleCase(summary),
    children: <>{getEUValue(data?.fiber_summary?.[summary]?.value ?? data?.fiber_summary?.[summary])}</>,
  }))


  const networkStatsItems = Object.keys(data?.network_stats ?? {}).filter((item) => item !== "iteration_stats").map((summary) => ({
    key: summary,
    label: toTitleCase(summary),
    children: <>{getEUValue(data?.network_stats?.[summary])}</>,
  }))

  const items = [
    {
      key: 'fiber_summary',
      label: toTitleCase('fiber_summary'),
      children: <Descriptions items={fiberSummaryItems} bordered />,
    },
    {
      key: 'network_stats',
      label: toTitleCase('network_stats'),
      children: <Descriptions items={networkStatsItems} bordered />,
    },
  ]

  const phaseTwoColumns = [
    {
      title: "",
      dataIndex: "id",
      key: "id",
      render: (item: any) => <>{item?.replace("mask_id_", `${t("sem.mask")} `)}</>,
      width: "10%",
      fixed: "left" as "left",
    },
    {
      title: 'Intensity Gradient', dataIndex: 'intensity_gradient', key: 'intensity_gradient',
      render: (_: any, record: any) => {
        return <>{getEUValue(record?.intensity_properties?.intensity_gradient)}</>
      },
    },
    {
      title: 'Normalized Intensity', dataIndex: 'normalized_intensity', key: 'normalized_intensity',
      render: (_: any, record: any) => {
        return <>{getEUValue(record?.intensity_properties?.normalized_intensity)}</>
      },
    },
    {
      title: 'Length (µm)', dataIndex: 'length', key: 'length',
      render: (_: any, record: any) => {
        return <>{getEUValue(record?.length)}</>
      },

    },
    {
      title: 'Curvature', dataIndex: 'curvature', key: 'curvature',
      render: (_: any, record: any) => {
        return <>{getEUValue(record?.curvature?.value)}</>
      },

    },
    {
      title: 'Junction Points', dataIndex: 'junction_points', key: 'junction_points',
      width: 200,
      render: (_: any, record: any) => {
        const points = record?.junction_points?.map((point: any) => `(${getEUValue(point[0])},${getEUValue(point[1])})`)
        if (!!points?.length) return <Typography.Text ellipsis={{
          tooltip: points.join("-")
        }} style={{
          width: 200
        }}>
          {points.join("-")}
        </Typography.Text>
        return null
      },
    },
    {
      title: 'EndPoints', dataIndex: 'endpoints', key: 'endpoints',
      width: 200,
      render: (_: any, record: any) => {
        const points = record?.endpoints?.map((point: any) => `(${getEUValue(point[0])},${getEUValue(point[1])})`)
        if (!!points?.length) return <Typography.Text ellipsis={{
          tooltip: points.join("-")
        }} style={{
          width: 200
        }}>
          {points.join("-")}
        </Typography.Text>
        return null
      },
    },
    {
      title: 'Average Intensity', dataIndex: 'avg_intensity', key: 'avg_intensity',
      render: (_: any, record: any) => {
        return <>{getEUValue(record?.intensity_properties?.avg_intensity)}</>
      },

    },
    {
      title: 'Is Branched', dataIndex: 'is_branched', key: 'is_branched',
      render: (_: any, record: any) => {
        return <>{record?.is_branched ? "Yes" : "No"}</>
      },
    },
    {
      title: 'Is Straight', dataIndex: 'is_straight', key: 'is_straight',
      render: (_: any, record: any) => {
        return <>{record?.is_straight ? "Yes" : "No"}</>
      },
    },
    {
      title: 'Is Closed Loop', dataIndex: 'is_closed_loop', key: 'is_closed_loop',
      render: (_: any, record: any) => {
        return <>{record?.is_closed_loop ? "Yes" : "No"}</>
      },
    },
    {
      title: 'Complexity Score', dataIndex: 'complexity_score', key: 'complexity_score',
      render: (_: any, record: any) => {
        return <>{getEUValue(record?.complexity_score)}</>
      },

    },
    {
      title: 'Endpoint Distance (µm)', dataIndex: 'endpoint_distance', key: 'endpoint_distance',
      render: (_: any, record: any) => {
        return <>{getEUValue(record?.endpoint_distance?.value)}</>
      },

    },
  ];

  return <div style={{
    display: "flex",
    gap: "1rem",
    flexDirection: "column"
  }}>
    <Collapse items={items} bordered={false} defaultActiveKey={['fiber_summary', "network_stats"]} />
    <div>
      <h3>{"Mask Information"}</h3>
      <Table dataSource={fiberData} columns={phaseTwoColumns}
        scroll={{
          x: 450
        }}
      />
    </div>
  </div>

}

export default FiberAnalysisAttributes;
